import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { WarningCircle } from "@phosphor-icons/react";
import InboxDocumentViewer from "../Inbox/components/InboxDocumentViewer";
import { fetchEmbeddedDocument, resolverFor } from "../../service/embeddedDocument";

/**
 * The page Salesforce frames inside its "Prep for Conference" workspace:
 * one document, no app chrome, no sign-in. The pass in the address is the
 * credential (minted server to server for this document and this person;
 * thirty minutes). Nothing on this page talks to the authenticated API, so
 * nothing here can bounce the frame to the login screen.
 */
const Frame = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  background: #f5f6f8;
  & > section { flex: 1; min-width: 0; }
`;

const Notice = styled.div`
  margin: auto;
  max-width: 420px;
  padding: 24px;
  text-align: center;
  color: #4a5568;
  font-size: 15px;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const EmbeddedDocumentPage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, document: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, document: null });
    fetchEmbeddedDocument(token)
      .then((document) => {
        if (!cancelled) setState({ loading: false, error: null, document });
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error: error.message || "The document could not be opened.", document: null });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state.loading) {
    return (
      <Frame data-testid="embedded-document">
        <Notice>Opening the document…</Notice>
      </Frame>
    );
  }
  if (state.error) {
    return (
      <Frame data-testid="embedded-document">
        <Notice role="alert">
          <WarningCircle size={28} weight="fill" color="#C53030" aria-hidden="true" />
          <strong>{state.error}</strong>
          <span>Go back to Salesforce and open the document again.</span>
        </Notice>
      </Frame>
    );
  }
  const { document } = state;
  return (
    <Frame data-testid="embedded-document">
      <InboxDocumentViewer
        documentId={document.documentId}
        documentName={document.name || ""}
        resolveUrl={resolverFor(document.viewer)}
        reportTimings={false}
      />
    </Frame>
  );
};

export default EmbeddedDocumentPage;
